// src/wallpapers/DigitalMedia.jsx
import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { 
  FiSearch, 
  FiEdit, 
  FiTrash2, 
  FiPlus, 
  FiUpload,
  FiEye,
  FiEyeOff,
  FiImage,
  FiVideo,
  FiFile,
  FiCheck,
  FiX,
  FiChevronLeft,
  FiChevronRight
} from "react-icons/fi";
import PageHeader from "../components/PageHeader";
import CustomDataTable from "../components/DataTable";
import ContentModal from "../components/ContentModal";
import BulkUploadModal from "../components/BulkUploadModal";
import TagsInput from "../components/TagsInput";
import "./DigitalMedia.css";

export default function DigitalMedia({ darkMode }) {
    const [show, setShow] = useState(false);
    const [mediaData, setMediaData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingMedia, setEditingMedia] = useState(null);
    const [editForm, setEditForm] = useState({
        name: "",
        type: "image",
        tags: [],
        status: "unpublished",
    });
    const [editLoading, setEditLoading] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewMedia, setPreviewMedia] = useState(null);
    const [replacementFile, setReplacementFile] = useState(null);
    const [replacementPreviewUrl, setReplacementPreviewUrl] = useState(null);
    const [replacementUploadLoading, setReplacementUploadLoading] = useState(false);
    const [removeCurrentImage, setRemoveCurrentImage] = useState(false);
    const [replacementError, setReplacementError] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [search, setSearch] = useState("");

    const filteredData = mediaData.filter(item => {
        const searchableText = [
            item.name,
            item.type,
            ...(Array.isArray(item.tags) ? item.tags : []),
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        return searchableText.includes(search.toLowerCase());
    });

    // Get status counts
    const publishedCount = mediaData.filter(item => item.status === 'published').length;
    const unpublishedCount = mediaData.filter(item => item.status === 'unpublished').length;
    const imageCount = mediaData.filter(item => item.type === 'image' || !item.type).length;
    const videoCount = mediaData.filter(item => item.type === 'video').length;

    // ===== FETCH MEDIA DATA =====
    const fetchMediaData = async () => {
        try {
            setLoading(true);
            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_API_LOCAL 
                : process.env.REACT_APP_BACKEND_API;

                const reqBody = {
                    type:"image"
                }
            const res = await fetch(`${base_url}/api/home/admin-media-data`, {
               method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify(reqBody),
            });
            if (!res.ok) throw new Error(`Fetch failed ${res.status}`);
            const json = await res.json();
            const list = json.data || json;
            setMediaData(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Failed to fetch media data', err);
            setMediaData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMediaData();
    }, []);

    // ===== COLUMNS DEFINITION =====
    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    checked={filteredData.length > 0 && filteredData.every(item => selectedIds.includes(item._id))}
                    onChange={() => {
                        const allIds = filteredData.map(item => item._id);
                        const allSelected = allIds.every(id => selectedIds.includes(id));
                        if (allSelected) {
                            setSelectedIds([]);
                        } else {
                            setSelectedIds(allIds);
                        }
                    }}
                    disabled={bulkActionLoading || filteredData.length === 0}
                    className="checkbox-custom"
                />
            ),
            cell: row => (
                <input
                    type="checkbox"
                    checked={selectedIds.includes(row._id)}
                    onChange={() => {
                        setSelectedIds(prev => {
                            if (prev.includes(row._id)) {
                                return prev.filter(item => item !== row._id);
                            } else {
                                return [...prev, row._id];
                            }
                        });
                    }}
                    disabled={bulkActionLoading}
                    className="checkbox-custom"
                />
            ),
            width: "50px",
        },
        {
            name: "Preview",
            cell: row => {
                const isVideo = row.type === 'video' || /\.(mp4|mov|webm|avi)$/i.test(row.url);

                return (
                    <div
                        onClick={() => row.url && openPreviewModal(row)}
                        className="preview-thumbnail"
                        style={{ cursor: row.url ? "zoom-in" : "default" }}
                    >
                        {isVideo ? (
                            <video
                                src={row.url}
                                className="thumbnail-video"
                                muted
                                preload="metadata"
                                playsInline
                            />
                        ) : (
                            <img src={row.url} alt={row.name} className="thumbnail-image" />
                        )}
                    </div>
                );
            },
        },
        {
            name: "Name",
            selector: row => row.name,
            sortable: true,
            cell: row => <span className="media-name-cell">{row.name}</span>,
        },
        {
            name: "Type",
            cell: row => (
                <span className={`type-badge ${row.type}`}>
                    {row.type === 'video' ? <FiVideo size={12} /> : <FiImage size={12} />}
                    {row.type || 'image'}
                </span>
            ),
        },
        {
            name: "Status",
            cell: row => {
                const isUpdating = updatingStatusId === row._id;

                return (
                    <div className="status-select-wrapper">
                        <select
                            className={`status-select ${row.status || 'unpublished'}`}
                            value={row.status || "unpublished"}
                            onChange={(e) => handleStatusChange(row, e.target.value)}
                            disabled={isUpdating}
                        >
                            <option value="published">Published</option>
                            <option value="unpublished">Unpublished</option>
                        </select>
                        {isUpdating && (
                            <span className="status-updating">Updating...</span>
                        )}
                    </div>
                );
            },
            sortable: true,
        },
        {
            name: "Actions",
            cell: row => (
                <div className="action-buttons">
                    <button className="action-btn edit" onClick={() => openEditModal(row)}>
                        <FiEdit size={14} />
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(row._id)}>
                        <FiTrash2 size={14} />
                    </button>
                </div>
            ),
        },
    ];

    // ===== HANDLE STATUS CHANGE =====
    const handleStatusChange = async (row, newStatus) => {
        const previousStatus = row.status;
        setUpdatingStatusId(row._id);
        setMediaData((prev) =>
            prev.map((item) =>
                item._id === row._id ? { ...item, status: newStatus } : item
            )
        );

        try {
            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_API_LOCAL 
                : process.env.REACT_APP_BACKEND_API;
                
            const res = await fetch(`${base_url}/api/home/admin-media-data/${row._id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });
            
            if (!res.ok) throw new Error(`Update failed ${res.status}`);
            await fetchMediaData();
        } catch (err) {
            console.error('Failed to update media status', err);
            setMediaData((prev) =>
                prev.map((item) =>
                    item._id === row._id ? { ...item, status: previousStatus } : item
                )
            );
        } finally {
            setUpdatingStatusId(null);
        }
    };

    // ===== HANDLE BULK ACTION =====
    const handleBulkAction = async (action) => {
        if (selectedIds.length === 0) {
            alert("Please select at least one item.");
            return;
        }

        let confirmMessage = "";
        let successMessage = "";

        switch (action) {
            case "publish":
                confirmMessage = `Are you sure you want to publish ${selectedIds.length} item(s)?`;
                successMessage = "Items published successfully!";
                break;
            case "unpublish":
                confirmMessage = `Are you sure you want to unpublish ${selectedIds.length} item(s)?`;
                successMessage = "Items unpublished successfully!";
                break;
            case "delete":
                confirmMessage = `Are you sure you want to delete ${selectedIds.length} item(s)? This action cannot be undone.`;
                successMessage = "Items deleted successfully!";
                break;
            default:
                return;
        }

        if (!window.confirm(confirmMessage)) return;

        setBulkActionLoading(true);
        try {
            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_API_LOCAL 
                : process.env.REACT_APP_BACKEND_API;

            let endpoint = `${base_url}/api/home/admin-media-data/bulk`;
            let method = "POST";
            let body = JSON.stringify({ ids: selectedIds });

            if (action === "delete") {
                method = "DELETE";
            }

            const res = await fetch(endpoint, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: body,
            });

            if (!res.ok) throw new Error(`Bulk action failed ${res.status}`);

            await fetchMediaData();
            setSelectedIds([]);
            alert(successMessage);
        } catch (err) {
            console.error(`Failed to perform bulk ${action}`, err);
            alert(`Failed to ${action} items. Please try again.`);
        } finally {
            setBulkActionLoading(false);
        }
    };

    // ===== UPLOAD REPLACEMENT FILE =====
    const uploadReplacementFile = async (file) => {
        if (!file) return null;

        setReplacementUploadLoading(true);
        setReplacementError(null);

        try {
            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_API_LOCAL 
                : process.env.REACT_APP_BACKEND_API;
                
            const formData = new FormData();
            formData.append("image", file);

            const res = await fetch(`${base_url}/api/r2/upload`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: formData,
            });

            if (!res.ok) throw new Error(`Upload failed ${res.status}`);
            const json = await res.json();
            const data = json.data || json;
            return {
                name: data.name || data.fileName || file.name,
                url: data.url || data.imageUrl || data.fileUrl,
                fileType: file.type?.startsWith("video") ? "video" : "image",
            };
        } catch (err) {
            console.error("Failed to upload replacement file", err);
            setReplacementError(err.message || "Failed to upload replacement file.");
            return null;
        } finally {
            setReplacementUploadLoading(false);
        }
    };

    // ===== OPEN EDIT MODAL =====
    const openEditModal = (row) => {
        setEditingMedia(row);
        setEditForm({
            name: row.name || "",
            type: row.type || "image",
            tags: Array.isArray(row.tags) ? row.tags : [],
            status: row.status || "unpublished",
        });
        setReplacementFile(null);
        setReplacementPreviewUrl(null);
        setReplacementError(null);
        setRemoveCurrentImage(false);
        setEditModalOpen(true);
    };

    // ===== CLOSE EDIT MODAL =====
    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditingMedia(null);
        setEditForm({ name: "", type: "image", tags: [], status: "unpublished" });
        setReplacementFile(null);
        setReplacementPreviewUrl(null);
        setReplacementError(null);
        setRemoveCurrentImage(false);
    };

    // ===== OPEN PREVIEW MODAL =====
    const openPreviewModal = (row) => {
        setPreviewMedia(row);
        setPreviewModalOpen(true);
    };

    // ===== CLOSE PREVIEW MODAL =====
    const closePreviewModal = () => {
        setPreviewModalOpen(false);
        setPreviewMedia(null);
    };

    // ===== HANDLE EDIT SUBMIT =====
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editingMedia?._id) return;

        try {
            setEditLoading(true);

            if (removeCurrentImage) {
                const base_url = process.env.REACT_APP_ENV === 'local' 
                    ? process.env.REACT_APP_BACKEND_API_LOCAL 
                    : process.env.REACT_APP_BACKEND_API;
                    
                const deleteRes = await fetch(`${base_url}/api/home/admin-media-data/${editingMedia._id}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                    },
                });
                if (!deleteRes.ok) throw new Error(`Delete failed ${deleteRes.status}`);
                await fetchMediaData();
                closeEditModal();
                return;
            }

            let uploadedFileData = null;
            if (replacementFile) {
                uploadedFileData = await uploadReplacementFile(replacementFile);
                if (!uploadedFileData) return;
            }

            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_API_LOCAL 
                : process.env.REACT_APP_BACKEND_API;
                
            const res = await fetch(`${base_url}/api/home/admin-media-data/${editingMedia._id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify({
                    name: uploadedFileData?.name || editForm.name.trim(),
                    type: uploadedFileData?.fileType || editForm.type,
                    tags: editForm.tags,
                    status: editForm.status,
                }),
            });

            if (!res.ok) throw new Error(`Edit failed ${res.status}`);
            await fetchMediaData();
            closeEditModal();
        } catch (err) {
            console.error('Failed to update media', err);
        } finally {
            setEditLoading(false);
        }
    };

    // ===== HANDLE DELETE =====
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this media?")) {
            return;
        }
        try {
            setLoading(true);
            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_API_LOCAL 
                : process.env.REACT_APP_BACKEND_API;
                
            const res = await fetch(`${base_url}/api/home/admin-media-data/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
            });
            if (!res.ok) throw new Error(`Delete failed ${res.status}`);
            await fetchMediaData();
        } catch (err) {
            console.error('Failed to delete media', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`digital-media-container ${darkMode ? 'dark' : ''}`}>
            <div className="digital-media-content">
                {/* Page Header */}
                <div className="page-header-wrapper">
                    <div className="header-left">
                        <h1 className="page-title">Digital Media</h1>
                        <p className="page-subtitle">Manage your digital media assets</p>
                    </div>
                    <div className="header-actions">
                        <button 
                            className="btn-bulk-upload"
                            onClick={() => setBulkUploadOpen(true)}
                        >
                            <FiUpload size={16} />
                            Bulk Upload
                        </button>
                        <button 
                            className="btn-add-media"
                            onClick={() => setShow(true)}
                        >
                            <FiPlus size={16} />
                            Add Media
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon total">
                            <FiFile size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Total Media</span>
                            <span className="stat-value">{mediaData.length}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon published">
                            <FiEye size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Published</span>
                            <span className="stat-value">{publishedCount}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon unpublished">
                            <FiEyeOff size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Unpublished</span>
                            <span className="stat-value">{unpublishedCount}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon images">
                            <FiImage size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Images</span>
                            <span className="stat-value">{imageCount}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon videos">
                            <FiVideo size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Videos</span>
                            <span className="stat-value">{videoCount}</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="table-card-modern">
                    {/* Toolbar */}
                    <div className="table-toolbar">
                        <div className="search-wrapper">
                            <FiSearch className="search-icon" />
                            <input
                                className="search-input-modern"
                                placeholder="Search media by name, type, or tags..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="toolbar-actions">
                            {selectedIds.length > 0 && (
                                <div className="bulk-actions">
                                    <span className="selected-count">
                                        <FiCheck size={14} />
                                        {selectedIds.length} selected
                                    </span>
                                    <button 
                                        className="bulk-action-btn publish"
                                        onClick={() => handleBulkAction("publish")}
                                        disabled={bulkActionLoading}
                                    >
                                        <FiEye size={14} />
                                        Publish
                                    </button>
                                    <button 
                                        className="bulk-action-btn unpublish"
                                        onClick={() => handleBulkAction("unpublish")}
                                        disabled={bulkActionLoading}
                                    >
                                        <FiEyeOff size={14} />
                                        Unpublish
                                    </button>
                                    <button 
                                        className="bulk-action-btn delete"
                                        onClick={() => handleBulkAction("delete")}
                                        disabled={bulkActionLoading}
                                    >
                                        <FiTrash2 size={14} />
                                        Delete
                                    </button>
                                    <button 
                                        className="bulk-action-btn clear"
                                        onClick={() => setSelectedIds([])}
                                        disabled={bulkActionLoading}
                                    >
                                        <FiX size={14} />
                                        Clear
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-wrapper-modern">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading media...</p>
                            </div>
                        ) : (
                            <CustomDataTable
                                columns={columns}
                                data={filteredData}
                            />
                        )}
                    </div>

                    {/* Footer */}
                    <div className="table-footer-modern">
                        <span className="table-info">
                            Showing {filteredData.length} of {mediaData.length} entries
                        </span>
                        <div className="pagination-modern">
                            <button className="page-btn" disabled>
                                <FiChevronLeft size={16} />
                            </button>
                            <button className="page-btn active">1</button>
                            <button className="page-btn">2</button>
                            <button className="page-btn">3</button>
                            <button className="page-btn">
                                <FiChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <ContentModal
                show={show}
                title="Add/Edit Content"
                onClose={() => setShow(false)}
                onSubmit={fetchMediaData}
            />

            <Modal show={editModalOpen} onHide={closeEditModal} centered className={`edit-modal ${darkMode ? 'dark' : ''}`}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Media</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={editForm.name}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                                required
                                className="form-control-modern"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Type</Form.Label>
                            <Form.Select
                                value={editForm.type}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, type: e.target.value }))}
                                className="form-control-modern"
                            >
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={editForm.status}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                                className="form-control-modern"
                            >
                                <option value="published">Published</option>
                                <option value="unpublished">Unpublished</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Replace Current Media</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*,video/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    setReplacementFile(file);
                                    setReplacementPreviewUrl(URL.createObjectURL(file));
                                }}
                                className="form-control-modern"
                            />
                            {replacementPreviewUrl && (
                                <div className="mt-2 preview-container">
                                    {replacementFile?.type?.startsWith("video") ? (
                                        <video
                                            src={replacementPreviewUrl}
                                            controls
                                            className="preview-media"
                                        />
                                    ) : (
                                        <img
                                            src={replacementPreviewUrl}
                                            alt="replacement preview"
                                            className="preview-media"
                                        />
                                    )}
                                </div>
                            )}
                            {replacementUploadLoading && (
                                <div className="text-muted small mt-2">Uploading replacement file...</div>
                            )}
                            {replacementError && (
                                <div className="text-danger small mt-2">{replacementError}</div>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Remove current image from the database"
                                checked={removeCurrentImage}
                                onChange={(e) => setRemoveCurrentImage(e.target.checked)}
                                className="checkbox-modern"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Tags</Form.Label>
                            <TagsInput
                                value={editForm.tags}
                                onChange={(tags) => setEditForm((prev) => ({ ...prev, tags }))}
                                placeholder="Add tags and press Enter"
                            />
                        </Form.Group>
                        <div className="modal-actions">
                            <Button variant="secondary" onClick={closeEditModal} disabled={editLoading}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={editLoading}>
                                {editLoading ? "Saving..." : "Save Changes"}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={previewModalOpen} onHide={closePreviewModal} centered size="lg" className={`preview-modal ${darkMode ? 'dark' : ''}`}>
                <Modal.Header closeButton>
                    <Modal.Title>{previewMedia?.name || "Media Preview"}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {previewMedia?.type === 'video' || /\.(mp4|mov|webm|avi)$/i.test(previewMedia?.url || "") ? (
                        <video
                            src={previewMedia?.url}
                            controls
                            autoPlay
                            className="preview-full"
                        />
                    ) : previewMedia?.url ? (
                        <img
                            src={previewMedia.url}
                            alt={previewMedia.name}
                            className="preview-full"
                        />
                    ) : null}
                </Modal.Body>
            </Modal>

            <BulkUploadModal
                show={bulkUploadOpen}
                onClose={() => setBulkUploadOpen(false)}
                onSuccess={() => {
                    fetchMediaData();
                    setBulkUploadOpen(false);
                }}
            />
        </div>
    );
}